import { useMemo, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  History,
  Search,
  ShieldCheck,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { formatDateTime, formatRelative } from '@/lib/format';
import {
  useAuditFacets,
  useAuditLogs,
  type AuditFilters,
  type AuditLogEntry,
} from './api';

// Helpers ----------------------------------------------------------------

const ACTION_LABEL: Record<string, string> = {
  // user/auth
  'user.login': 'Login',
  'user.password_changed': 'Senha alterada (pelo próprio)',
  'user.password_reset': 'Senha redefinida (via email)',
  'user.password_reset_by_admin': 'Senha redefinida (por admin)',
  'user.profile_updated': 'Perfil atualizado',
  'user.sessions_revoked': 'Sessões encerradas',
  'user.created': 'Usuário criado',
  'user.updated': 'Usuário atualizado',
  'user.deleted': 'Usuário removido',
  // tenant
  'tenant.registered': 'Empresa cadastrada',
  'tenant.updated': 'Dados da empresa atualizados',
  'tenant.billing_updated': 'Cobrança alterada',
  // invitation
  'invitation.created': 'Convite enviado',
  'invitation.accepted': 'Convite aceito',
  'invitation.cancelled': 'Convite cancelado',
  // vehicle/driver
  'vehicle.created': 'Veículo criado',
  'vehicle.updated': 'Veículo atualizado',
  'vehicle.deleted': 'Veículo removido',
  'driver.created': 'Motorista criado',
  'driver.updated': 'Motorista atualizado',
  'driver.deleted': 'Motorista removido',
  'driver_assignment.created': 'Vínculo criado',
  'driver_assignment.ended': 'Vínculo encerrado',
  // documents
  'vehicle_document.created': 'Documento de veículo anexado',
  'vehicle_document.deleted': 'Documento de veículo removido',
  'driver_document.created': 'Documento de motorista anexado',
  'driver_document.deleted': 'Documento de motorista removido',
  // checklist
  'checklist_template.created': 'Modelo de checklist criado',
  'checklist_template.updated': 'Modelo atualizado',
  'checklist_template.deleted': 'Modelo removido',
  'checklist_execution.completed': 'Checklist concluído',
  // fueling
  'fueling.created': 'Abastecimento registrado',
  'fueling.updated': 'Abastecimento atualizado',
  'fueling.deleted': 'Abastecimento removido',
  'fueling.imported': 'Abastecimentos importados',
  // maintenance
  'maintenance_plan.created': 'Plano de manutenção criado',
  'maintenance_plan.updated': 'Plano atualizado',
  'maintenance_plan.deleted': 'Plano removido',
  'maintenance_order.created': 'OS criada',
  'maintenance_order.updated': 'OS atualizada',
  'maintenance_order.deleted': 'OS removida',
  'maintenance_order.approved': 'OS aprovada',
  'maintenance_order.started': 'OS iniciada',
  'maintenance_order.completed': 'OS concluída',
  'maintenance_order.cancelled': 'OS cancelada',
  // fines
  'fine.created': 'Multa registrada',
  'fine.updated': 'Multa atualizada',
  'fine.deleted': 'Multa removida',
  'fine.driver_indicated': 'Condutor indicado',
  'fine.company_responsibility': 'Empresa assumiu multa',
  'fine.paid': 'Multa paga',
  'fine.cancelled': 'Multa cancelada',
};

function actionLabel(action: string): string {
  return ACTION_LABEL[action] ?? action;
}

function actionTone(
  action: string,
): 'success' | 'destructive' | 'warning' | 'info' | 'secondary' {
  if (action.endsWith('.deleted') || action === 'fine.cancelled')
    return 'destructive';
  if (action.includes('password') || action.includes('sessions_revoked'))
    return 'warning';
  if (action.endsWith('.created') || action.includes('.completed'))
    return 'success';
  if (action === 'user.login') return 'secondary';
  return 'info';
}

const ENTITY_LABEL: Record<string, string> = {
  user: 'Usuário',
  tenant: 'Empresa',
  invitation: 'Convite',
  vehicle: 'Veículo',
  driver: 'Motorista',
  driver_assignment: 'Vínculo',
  vehicle_document: 'Doc. veículo',
  driver_document: 'Doc. motorista',
  checklist_template: 'Modelo de checklist',
  checklist_execution: 'Execução de checklist',
  fueling: 'Abastecimento',
  maintenance_plan: 'Plano de manutenção',
  maintenance_order: 'OS de manutenção',
  fine: 'Multa',
};

const ROLE_LABEL: Record<string, string> = {
  admin: 'Administrador',
  manager: 'Gestor',
  driver: 'Motorista',
  workshop: 'Oficina',
};

function csvEscape(val: unknown): string {
  if (val === null || val === undefined) return '';
  const s = typeof val === 'object' ? JSON.stringify(val) : String(val);
  if (/[;"\n\r]/.test(s)) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

function downloadCSV(items: AuditLogEntry[], filename: string) {
  const headers = [
    'Data/Hora',
    'Ação',
    'Entidade',
    'ID da entidade',
    'Ator (nome)',
    'Ator (email)',
    'Papel',
    'IP',
    'User-Agent',
  ];
  const rows = items.map((e) => [
    formatDateTime(e.createdAt),
    actionLabel(e.action) + ` (${e.action})`,
    ENTITY_LABEL[e.entityType] ?? e.entityType,
    e.entityId,
    e.actor?.name ?? '',
    e.actor?.email ?? '',
    e.actor ? ROLE_LABEL[e.actor.role] ?? e.actor.role : '',
    e.ip ?? '',
    e.userAgent ?? '',
  ]);
  const csv = [
    headers.join(';'),
    ...rows.map((r) => r.map(csvEscape).join(';')),
  ].join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Page ------------------------------------------------------------------

export function AuditPage() {
  const [search, setSearch] = useState('');
  const [action, setAction] = useState<string>('all');
  const [entityType, setEntityType] = useState<string>('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<AuditLogEntry | null>(null);
  const limit = 50;

  const debouncedSearch = useDebouncedValue(search, 300);

  const filters: AuditFilters = useMemo(
    () => ({
      page,
      limit,
      q: debouncedSearch || undefined,
      action: action === 'all' ? undefined : action,
      entityType: entityType === 'all' ? undefined : entityType,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    }),
    [debouncedSearch, action, entityType, fromDate, toDate, page],
  );

  const query = useAuditLogs(filters);
  const facets = useAuditFacets();

  const items = query.data?.data ?? [];
  const total = query.data?.meta.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const resetFilters = () => {
    setSearch('');
    setAction('all');
    setEntityType('all');
    setFromDate('');
    setToDate('');
    setPage(1);
  };

  const handleExport = () => {
    // Exporta a página atual (com filtros aplicados).
    const dateStr = new Date().toISOString().slice(0, 10);
    downloadCSV(items, `frota-auditoria-${dateStr}.csv`);
  };

  return (
    <div className="page">
      <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1.5">
          <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <ShieldCheck className="size-3.5" /> Administração
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">Auditoria</h1>
          <p className="text-sm text-muted-foreground">
            Histórico de ações realizadas no sistema. Quem fez o quê e quando.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={items.length === 0}
        >
          <Download className="size-4" />
          Exportar CSV
        </Button>
      </header>

      {/* Filtros */}
      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Buscar por ação, entidade, ID ou nome do usuário..."
              className="pl-9"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-4">
            <div className="space-y-1.5">
              <Label>Ação</Label>
              <Select
                value={action}
                onValueChange={(v) => {
                  setAction(v);
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as ações</SelectItem>
                  {(facets.data?.actions ?? []).map((a) => (
                    <SelectItem key={a.value} value={a.value}>
                      {actionLabel(a.value)}{' '}
                      <span className="text-muted-foreground">({a.count})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Tipo de entidade</Label>
              <Select
                value={entityType}
                onValueChange={(v) => {
                  setEntityType(v);
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {(facets.data?.entityTypes ?? []).map((e) => (
                    <SelectItem key={e.value} value={e.value}>
                      {ENTITY_LABEL[e.value] ?? e.value}{' '}
                      <span className="text-muted-foreground">({e.count})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="from">De</Label>
              <Input
                id="from"
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="to">Até</Label>
              <Input
                id="to"
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>

          {(search ||
            action !== 'all' ||
            entityType !== 'all' ||
            fromDate ||
            toDate) && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                {total.toLocaleString('pt-BR')} resultado
                {total === 1 ? '' : 's'} encontrado{total === 1 ? '' : 's'}
              </span>
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                Limpar filtros
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          {query.isLoading ? (
            <div className="space-y-3 p-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <History className="size-10 text-muted-foreground" />
              <p className="text-sm font-medium">Nenhum evento encontrado</p>
              <p className="text-xs text-muted-foreground">
                Ajuste os filtros ou aguarde novas atividades no sistema.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Quando</th>
                    <th className="px-4 py-3 font-medium">Ator</th>
                    <th className="px-4 py-3 font-medium">Ação</th>
                    <th className="px-4 py-3 font-medium">Entidade</th>
                    <th className="px-4 py-3 font-medium">IP</th>
                    <th className="w-10 px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {items.map((e) => (
                    <tr key={e.id} className="transition-colors hover:bg-muted/30">
                      <td className="px-4 py-3 text-xs">
                        <p className="font-medium">{formatDateTime(e.createdAt)}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {formatRelative(e.createdAt)}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {e.actor ? (
                          <div>
                            <p className="truncate font-medium">{e.actor.name}</p>
                            <p className="truncate text-[11px] text-muted-foreground">
                              {ROLE_LABEL[e.actor.role] ?? e.actor.role}
                            </p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Sistema</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          variant={actionTone(e.action)}
                          className="text-[10px]"
                        >
                          {actionLabel(e.action)}
                        </Badge>
                        <p className="mt-1 font-mono text-[10px] text-muted-foreground">
                          {e.action}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <p className="font-medium">
                          {ENTITY_LABEL[e.entityType] ?? e.entityType}
                        </p>
                        <p className="truncate font-mono text-[10px] text-muted-foreground">
                          {e.entityId.length > 14
                            ? e.entityId.slice(0, 8) + '…' + e.entityId.slice(-4)
                            : e.entityId}
                        </p>
                      </td>
                      <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                        {e.ip ?? '—'}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          onClick={() => setDetail(e)}
                          title="Ver detalhes"
                        >
                          <Eye className="size-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Paginação */}
      {total > limit && (
        <div className="flex items-center justify-between text-xs">
          <p className="text-muted-foreground">
            Mostrando {(page - 1) * limit + 1}–{Math.min(page * limit, total)} de{' '}
            {total.toLocaleString('pt-BR')}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="size-4" />
              Anterior
            </Button>
            <span className="text-muted-foreground">
              Página {page} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Próxima
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Dialog de detalhes */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalhes do evento</DialogTitle>
            <DialogDescription>
              {detail ? formatDateTime(detail.createdAt) : ''}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid gap-3 sm:grid-cols-2">
                <DetailRow
                  label="Ação"
                  value={
                    <div>
                      <Badge
                        variant={actionTone(detail.action)}
                        className="text-[10px]"
                      >
                        {actionLabel(detail.action)}
                      </Badge>
                      <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                        {detail.action}
                      </p>
                    </div>
                  }
                />
                <DetailRow
                  label="Entidade"
                  value={
                    <div>
                      <p>{ENTITY_LABEL[detail.entityType] ?? detail.entityType}</p>
                      <p className="font-mono text-[11px] text-muted-foreground">
                        {detail.entityId}
                      </p>
                    </div>
                  }
                />
                <DetailRow
                  label="Ator"
                  value={
                    detail.actor ? (
                      <div>
                        <p className="font-medium">{detail.actor.name}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {detail.actor.email} ·{' '}
                          {ROLE_LABEL[detail.actor.role] ?? detail.actor.role}
                        </p>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">Sistema</span>
                    )
                  }
                />
                <DetailRow
                  label="Origem"
                  value={
                    <div>
                      <p className="font-mono text-[11px]">{detail.ip ?? '—'}</p>
                      {detail.userAgent && (
                        <p className="truncate text-[11px] text-muted-foreground">
                          {detail.userAgent}
                        </p>
                      )}
                    </div>
                  }
                />
              </div>

              {(detail.before != null || detail.after != null) && (
                <div className="space-y-3">
                  <div className="border-t border-border pt-3" />
                  {detail.before != null && (
                    <div>
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Antes
                      </p>
                      <JsonBlock data={detail.before} />
                    </div>
                  )}
                  {detail.after != null && (
                    <div>
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Depois
                      </p>
                      <JsonBlock data={detail.after} />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setDetail(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-border bg-muted/20 p-3">
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <div className="text-sm">{value}</div>
    </div>
  );
}

function JsonBlock({ data }: { data: unknown }) {
  const text = useMemo(() => {
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  }, [data]);
  return (
    <pre className="max-h-72 overflow-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-[11px]">
      {text}
    </pre>
  );
}
